import { PrivacyEmbedConsent } from '#components/PrivacyEmbedConsent';
import { BreakpointMedia } from '#cutils/breakpoints/media';
import { useEmbedAPI } from '#cutils/hooks/useEmbedAPI';
import { logger } from '#utils/logging/logger';

import styles from './ArticleModuleEmbed.module.scss';
import {
	getCmpServiceId,
	getEmbedAltText,
	getEmbedPrivacyText,
	getEmbedService,
	getEmbedSource,
	getEmbedUrl,
} from './ArticleModuleEmbed.utils';
import { EBUEmbed } from './EbuNewsEmbed';
import sectionStyles from './SectionStyles.module.scss';
import type { ArticleModuleEmbedFragment } from './__generated__/ArticleModuleEmbed.generated';

declare global {
	interface Window {
		NewsPilotReco?: () => void;
	}
}

type Props = {
	embedModule: ArticleModuleEmbedFragment;
	articleId: string;
};

/**
 * Adds the 'height' parameter with the value 'full' to the given URL.
 *
 * @param url The URL to modify.
 * @return The modified URL with the 'height' parameter added.
 */
function addHeightFullParameter(url: string) {
	const parsedUrl = new URL(url);
	parsedUrl.searchParams.set('height', 'full');
	return parsedUrl.toString();
}

export function ArticleModuleEmbed({ embedModule, articleId }: Props) {
	const altText = getEmbedAltText(embedModule);
	const service = getEmbedService(embedModule);
	const text = getEmbedPrivacyText(embedModule);
	const url = getEmbedUrl(embedModule);
	const source = getEmbedSource(embedModule) ?? '';
	const cmpId = getCmpServiceId(embedModule);

	const { height, isEmbedInitialized, isInitialized, iframeRef } = useEmbedAPI(url, embedModule.rowId, {
		type: 'article',
		id: articleId,
	});

	if (!altText) {
		logger.error(`Missing field 'altText' for embed: rowId: '%s'.`, embedModule.rowId);
		return null;
	}

	if (!service) {
		logger.error(`Missing field 'service' for embed: rowId: '%s'.`, embedModule.rowId);
		return null;
	}

	if (!source) {
		logger.error(`Missing fields 'source' for embed: rowId: '%s'.`, embedModule.rowId);
		return null;
	}

	if (!url) {
		logger.error(`Missing field 'url' for embed: rowId: '%s'.`, embedModule.rowId);
		return null;
	}

	const title = embedModule.embed?.embedService?.name ?? altText;
	const hasNoVisibleHeight = service !== 'ebu' && !height;

	return (
		<PrivacyEmbedConsent service={service} text={text} title={title} cmpId={cmpId} className={sectionStyles.section}>
			<section className={`${styles.wrapper} ${sectionStyles.section} ${hasNoVisibleHeight ? styles.noMargin : ''}`}>
				<div className={styles.root} style={{ opacity: !isEmbedInitialized && service !== 'ebu' ? 0 : undefined }}>
					{service === 'ebu' ? (
						<EBUEmbed source={source} />
					) : (
						<>
							{isInitialized && (
								<iframe
									ref={iframeRef}
									frameBorder="0"
									style={{ height: height == undefined ? undefined : `${height}px`, width: '100%' }}
									key={url}
									src={url}
									title={altText || 'Embed'}
									className={styles.iframe}
								/>
							)}
						</>
					)}

					{service === 'factfox' && (
						<BreakpointMedia lessThan="lg">
							{/*
                            For the mobile view we need the following code, because the factfox embed is a scrollable iframe,
                            we dont want scroll on the mobile view. Instead we make a read more link here and open
                            the iframe with a full height in a new tab.
                        */}
							{(className, renderChildren) => {
								return (
									<div className={`${className} ${styles.overlay}`} style={{ height: `${height}px`, width: '100%' }}>
										{renderChildren ? (
											<a className={styles.link} href={addHeightFullParameter(url)} target="_blank" rel="noopener noreferrer">
												Weiterlesen
											</a>
										) : null}
									</div>
								);
							}}
						</BreakpointMedia>
					)}
				</div>
			</section>
		</PrivacyEmbedConsent>
	);
}
